import {
  Autocomplete,
  Box,
  Checkbox,
  FormControlLabel,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import { FormikProps } from 'formik/dist/types';
import { useCallback, useEffect, useState } from 'react';
import useSWR from 'swr';

import { AlertModal } from '@shared/components/modals/AlertModal';
import { SERVICES } from '@shared/constants/apiConfig';
import { CancelButton, SaveButton } from '@shared/inputs/Buttons';
import { TextInput } from '@shared/inputs/TextInput';
import { CommonButtonWrapper } from '@styles/CommonStyles';

import { ILenderListItem } from '../../../Tools/shared/LenderControlsHeader';
import { allParamsList, configListByParamType, queueEmailRoles } from '../constants';
import { Config, ILeadSourceConfiguration, IQueueConfiguration, IRole } from '../types';
import QueueTableRow from './QueueTableRow';

const getConfig = (values: IQueueConfiguration | undefined): Config[] => {
  return allParamsList
    .filter(
      (item) => configListByParamType[values?.paramsType as keyof typeof configListByParamType]?.includes(item.name),
    )
    .map((item) => {
      return { ...item, value: values?.[item.name as keyof typeof values] };
    });
};

interface IConfigurationParametersProps extends Partial<FormikProps<IQueueConfiguration>> {
  roles: IRole[] | undefined;
}

const ConfigurationParameters  = ({
  values,
  resetForm,
  setFieldValue,
  initialValues,
  errors,
  roles,
}: IConfigurationParametersProps) => {
  const { data: lendersFetched } = useSWR<ILenderListItem[]>(`${SERVICES.LENDER}/lenders`);
  const { data: leadSourceFetched } = useSWR<ILeadSourceConfiguration[]>(`${SERVICES.LEAD}/source`);


  const [filteredLenders, setFilteredLenders] = useState<ILenderListItem[]>([]);
  const [excludedLendersValue, setExcludedLendersValue] = useState<ILenderListItem[]>([]);
  const [includedLendersValue, setIncludedLendersValue] = useState<ILenderListItem[]>([]);
  const [leadSourcesValues, setLeadSourcesValues] = useState<ILeadSourceConfiguration[]>([]);

  const [isOpen, setIsOpen] = useState(false);

  const config = getConfig(values);

  useEffect(() => {
    const leadSourcesSet = new Set(values?.includedPrimaryLeadSourceIds);
    const savedLeadSourcesValues: ILeadSourceConfiguration[] = [];

    if (leadSourceFetched?.length) {
      leadSourceFetched.forEach((item) => {
        if (leadSourcesSet.has(item.id)) {
          savedLeadSourcesValues.push(item);
        }
      });
      setLeadSourcesValues(savedLeadSourcesValues);
    }
  }, [values?.includedPrimaryLeadSourceIds, leadSourceFetched]);

  useEffect(() => {
    const excludedLendersSet = new Set(values?.excludedLenderIds);
    const includedLendersSet = new Set(values?.includedLenderIds);

    const filteredLenders: ILenderListItem[] = [];
    const excludedLenders: ILenderListItem[] = [];
    const includedLenders: ILenderListItem[] = [];

    if (lendersFetched?.length) {
      lendersFetched.forEach((item) => {
        if (!excludedLendersSet.has(item.id) && !includedLendersSet.has(item.id)) {
          filteredLenders.push(item);
        } else if (excludedLendersSet.has(item.id)) {
          excludedLenders.push(item);
        } else if (includedLendersSet.has(item.id)) {
          includedLenders.push(item);
        }
      });
      setFilteredLenders(filteredLenders);
      setExcludedLendersValue(excludedLenders);
      setIncludedLendersValue(includedLenders);
    }
  }, [values?.excludedLenderIds, values?.includedLenderIds, lendersFetched]);

  useEffect(() => {
    if (values?.description === null) setFieldValue?.('description', '');
  }, [values?.description]);

  useEffect(() => {
    if (!values?.isQueueEmailRequired) setFieldValue?.('queueEmailRoles', []);
  }, [values?.isQueueEmailRequired]);

  const onConfirm = useCallback(() => {
    resetForm?.();
    setIsOpen(false);
  }, [initialValues]);

  return (
    <Grid item md={6}>
      <Stack direction="row" justifyContent="space-between" mb={3}>
        <Typography variant="h3" sx={{ fontWeight: 'bold', flex: '1 0 140px' }}>
          Configuration Parameters
        </Typography>

        <CommonButtonWrapper direction="row">
          <CancelButton label="Reset" onClick={() => setIsOpen(true)} />
          <SaveButton/>
        </CommonButtonWrapper>
      </Stack>

      <Stack direction="row" mb={3} sx={{ flexWrap: 'wrap' }}>
        <FormControlLabel
          name="isQueueEnabled"
          label="Queue Enabled"
          control={
            <Checkbox
              checked={Boolean(values?.isQueueEnabled)}
              onChange={(_, checked) => setFieldValue?.('isQueueEnabled', checked)}
            />
          }
        />

        <FormControlLabel
          name="isCountRequired"
          label="Is Count Required"
          control={
            <Checkbox
              checked={Boolean(values?.isCountRequired)}
              onChange={(_, checked) => setFieldValue?.('isCountRequired', checked)}
            />
          }
        />

        <FormControlLabel
          name="isDownPaymentDeclineQueue"
          label="Is Down Payment Decline Queue"
          control={
            <Checkbox
              checked={Boolean(values?.isDownPaymentDeclineQueue)}
              onChange={(_, checked) => setFieldValue?.('isDownPaymentDeclineQueue', checked)}
            />
          }
        />

        <FormControlLabel
          name="isBuyBackQueue"
          label="Is Buy Back Queue"
          control={
            <Checkbox
              checked={Boolean(values?.isBuyBackQueue)}
              onChange={(_, checked) => setFieldValue?.('isBuyBackQueue', checked)}
            />
          }
        />
      </Stack>

      <Stack direction="row" justifyContent="space-between" mb={3}>
        <Box style={{ width: '100%' }}>
          <Autocomplete
            options={roles || []}
            loading={!roles}
            value={roles?.find((item) => item.id === values?.roleId) || null}
            getOptionLabel={(option) => `[${option.numericId}] ${option.name}`}
            renderInput={(params) => <TextField {...params} label="Select Role" />}
            onChange={(_, value) => setFieldValue?.('roleId', value?.id)}
          />
        </Box>
      </Stack>

      <TextInput
        multiline
        name="description"
        maxRows={4}
        label="Description"
        value={values?.description || ''}
        onChange={(event) => setFieldValue?.('description', event?.target?.value)}
      />

      <Typography marginTop={4}>
        <b>{values?.paramsType}</b>
      </Typography>

      <Table size="small" aria-label="a dense table">
        <TableHead>
          <TableRow>
            <TableCell>Name</TableCell>
            <TableCell align="right">Options</TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {config.map((row) => (
            <QueueTableRow
              key={row?.name}
              setFieldValue={setFieldValue}
              row={row}
              initialValue={initialValues?.[row?.name as keyof typeof initialValues]}
              isError={Boolean(errors?.[row?.name as keyof typeof errors])}
            />
          ))}
        </TableBody>
      </Table>

      {values?.paramsType === 'DealIndexParams' && (
        <Stack marginTop={2}>
          <Autocomplete
            multiple
            sx={{ marginBottom: '10px' }}
            id="excludedLenderIds"
            options={filteredLenders || []}
            value={excludedLendersValue || null}
            getOptionLabel={(option) => `[${option.numericId}] ${option.name}`}
            renderInput={(params) => <TextField {...params} label="Excluded Lender" />}
            onChange={(_, value) => {
              setExcludedLendersValue(value);
              setFieldValue?.(
                'excludedLenderIds',
                value.map((item) => item.id)
              );
            }}
          />

          <Autocomplete
            multiple
            id="includedLenderIds"
            options={filteredLenders || []}
            value={includedLendersValue || null}
            getOptionLabel={(option) => `[${option.numericId}] ${option.name}`}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Included Lender"
              />
            )}
            onChange={(_, value) => {
              setIncludedLendersValue(value);
              setFieldValue?.(
                'includedLenderIds',
                value.map((item) => item.id)
              );
            }}
          />
        </Stack>
      )}

      {values?.paramsType === 'ContactIndexParams' && (
        <Stack marginTop={2}>
          <Autocomplete
            multiple
            id="includedPrimaryLeadSourceIds"
            options={leadSourceFetched || []}
            value={leadSourcesValues || null}
            getOptionLabel={(option) => `[${option.numericId}] ${option.name}`}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Included Lead source"
              />
            )}
            onChange={(_, value) => {
              setFieldValue?.(
                'includedPrimaryLeadSourceIds',
                value.map((item) => item?.id)
              );
            }}
          />
        </Stack>
      )}

      <Stack marginTop={2}>
        <FormControlLabel
          name="isQueueEmailRequired"
          label="Enable Email Alert"
          style={{ marginBottom: '20px' }}
          control={
            <Checkbox
              checked={Boolean(values?.isQueueEmailRequired)}
              onChange={(_, checked) => setFieldValue?.('isQueueEmailRequired', checked)}
            />
          }
        />

        <Autocomplete
          key={values?.queueEmailRoles.length}
          multiple
          filterSelectedOptions
          id="queueEmailRoles"
          options={queueEmailRoles}
          disabled={!values?.isQueueEmailRequired}
          value={values?.queueEmailRoles || []}
          renderInput={(params) => (
            <TextField error={Boolean(errors?.queueEmailRoles)} {...params} label="Select email" />
          )}
          onChange={(_, value) => setFieldValue?.('queueEmailRoles', value)}
        />
      </Stack>
      { isOpen && 
        <AlertModal
          open={isOpen}
          title="Alert"
          subtitle="Are you sure you want to discard all changes?"
          buttonText="Ok"
          onClose={() => setIsOpen(false)}
          onConfirm={onConfirm}
        />
      }
    </Grid>
  );
};

export default ConfigurationParameters;
